import { useState, useEffect, useRef, useCallback } from "react";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { markdownToHtml } from "@/zapi-frontend/utils/markdown";
import { useSearchParams, useParams } from "react-router-dom";
import { AppButton, ClickAwayListener } from "@libs/zapi-ui/custom";
import { useScopedTimeout } from "@/zapi-frontend/shared/hooks/useScopedTimeout";
import { TChapter } from "@/zapi-frontend/shared/types/projects";
import { SidebarChapterItem } from "./SidebarChapterItem";
import { SidebarDashboardItem } from "./SidebarDashboardItem";
import { CaretRightIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { scrollToHeader } from "../../lib/scrollToHeader";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { useNotificationStore } from "@/zapi-frontend/features/Notifications";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { InstructionsIcon } from "@libs/zapi-ui/assets";

type TSidebarIslandProps = {
  chapterMemoPolling: {
    chapters: Array<{
      id: number;
      hasActiveMemo: boolean;
      chapterName: string;
    }>;
    isPolling: boolean;
  };
};

export function SidebarIsland({ chapterMemoPolling }: TSidebarIslandProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { id: projectId } = useParams() as { id: string };

  const { isFetchProjectLoading } = useProjectStore();
  const { getLoadingState } = useChatStore();
  const {
    projectChapters,
    activeChapter,
    setActiveChapter,
    isOpenChapterSidebar,
    toggleChapterSidebar,
    viewMode,
    setViewMode,
    sidebarChaptersData,
    setSidebarChaptersData,
    activeChapterSubsection,
    setActiveChapterSubsection
  } = useChapterStore();

  const { isLabs } = useMeStore();
  const { closeHandler } = useProjectSidebarStore();
  const { hasChapterNotification } = useNotificationStore();

  const { runTimeout } = useScopedTimeout();

  const headerCache = useRef(new Map<string, { content: string; headers: string[] }>());

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const extractHeaders = async (chapter: TChapter) => {
    const key = chapter.id.toString();
    const mdContent = chapter.activeMemo?.mdContent || "";

    const cached = headerCache.current.get(key);
    if (cached && cached.content === mdContent) return cached.headers;

    if (!mdContent) {
      headerCache.current.set(key, { content: mdContent, headers: [] });
      return [];
    }

    const htmlContent = await markdownToHtml(mdContent);

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const headingElements = tempDiv.querySelectorAll("h1, h2");
    const headers = Array.from(headingElements)
      .map((el) => el.textContent || "")
      .filter(Boolean);
    headerCache.current.set(key, { content: mdContent, headers });
    return headers;
  };

  const handleSubsectionClick = (chapterId: string, subsection: string) => {
    if (activeChapter?.id.toString() !== chapterId) {
      handleChapterChange(chapterId);
      runTimeout(() => scrollToHeader(subsection), 300);
    } else scrollToHeader(subsection);

    setActiveChapterSubsection(subsection);

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "document");
        params.set("chapterId", chapterId);
        params.set("chapterSubsection", subsection);
        return params;
      },
      { replace: true }
    );
  };

  const scrollToTopOfEditor = () => {
    const editorElement = document.querySelector(".ProseMirror");
    if (editorElement) {
      editorElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleChapterChange = useCallback(
    (chapterId: string) => {
      if (isMobile) toggleChapterSidebar(false);
      setExpandedIds((prev) => {
        if (prev.has(chapterId)) return new Set();
        return new Set([chapterId]);
      });
      if (activeChapter?.id.toString() === chapterId) return;

      setActiveChapter(chapterId);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "document");
        params.set("chapterId", chapterId);
        params.delete("chapterSubsection");
        params.delete("view");
        return params;
      });

      runTimeout(() => {
        scrollToTopOfEditor();
      }, 200);
    },
    [activeChapter?.id, setActiveChapter, setSearchParams, runTimeout, isMobile, toggleChapterSidebar]
  );
  const toggleInstructions = () => {
    if (viewMode === "instructions") {
      setViewMode("chapter");
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete("view");
          return params;
        },
        { replace: true }
      );
    } else {
      setViewMode("instructions");
      closeHandler();
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("view", "instructions");
          return params;
        },
        { replace: false }
      );
    }
  };

  const toggleDashboard = () => {
    const shouldOpenDashboard = viewMode !== "dashboard" && viewMode !== "projectOverview";

    if (shouldOpenDashboard) {
      setViewMode("dashboard");
      closeHandler();
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "overview");
        params.delete("chapterId");
        params.delete("chapterSubsection");
        params.delete("view");
        return params;
      });
    } else {
      setViewMode("chapter");
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.delete("view");
        return params;
      });
    }
  };

  const toggleSidebar = () => {
    toggleChapterSidebar(!isOpenChapterSidebar);
  };

  const onClickAway = () => {
    if (!isOpenChapterSidebar) return;
    toggleChapterSidebar(false);
  };

  useEffect(() => {
    const syncSidebar = async () => {
      const chaptersWithHeaders = await Promise.all(
        projectChapters.map(async (chapter) => ({
          id: chapter.id.toString(),
          title: chapter.projectTemplateChapter.name,
          icon: chapter.projectTemplateChapter.icon,
          completenessRatio: chapter.completenessRatio,
          subsections: await extractHeaders(chapter),
          totalQuestions: chapter.totalQuestions,
          validQuestionsCount: chapter.validQuestionsCount,
          isHidden: chapter.isHidden
        }))
      );

      const activeChapterId = searchParams.get("chapterId");

      setSidebarChaptersData(chaptersWithHeaders, activeChapterId);
    };

    syncSidebar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectChapters]);

  useEffect(() => {
    const chapterFromQuery = searchParams.get("chapterId");
    if (chapterFromQuery && chapterFromQuery !== activeChapter?.id?.toString()) {
      const chapterFromQueryObj = projectChapters.find((chapter) => chapter.id.toString() === chapterFromQuery);

      if (chapterFromQueryObj && (!chapterFromQueryObj.isHidden || isLabs)) {
        setActiveChapter(chapterFromQuery);
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.add(chapterFromQuery);
          return next;
        });
      }
    }
  }, [projectChapters, activeChapter?.id, isLabs]);

  useEffect(() => {
    const subsectionFromQuery = searchParams.get("chapterSubsection");
    if (subsectionFromQuery && activeChapter?.id) {
      setActiveChapterSubsection(subsectionFromQuery);
      runTimeout(() => scrollToHeader(subsectionFromQuery), 300);
    }
  }, [activeChapter?.id]);

  const isAnyOfTheChaptersLoading = sidebarChaptersData.some((chapter) => {
    const chapterMemoStatus = chapterMemoPolling.chapters.find((c) => c.id === Number(chapter.id));
    const hasActiveMemo = chapterMemoStatus?.hasActiveMemo ?? true;
    const { pendingGenerationId, isGeneratingChapter, isChapterLoaded } = getLoadingState(projectId, chapter.id);
    const isChatLoading = (!!pendingGenerationId || isGeneratingChapter) && !isChapterLoaded;
    return (!hasActiveMemo && chapterMemoPolling.isPolling) || isChatLoading;
  });

  if (isFetchProjectLoading) return null;

  return (
    <ClickAwayListener insideSelectors={["[data-chapter-sidebar-toggle]"]} onClickAway={onClickAway}>
      <div
        data-testid="sidebar-chapters-root"
        className={cn(
          "absolute top-16 -left-20 z-10 w-[56px] h-[calc(100svh-129px)] flex flex-col items-center py-3 px-1 bg-white transition-all duration-300",
          "hd:h-[calc(100vh-72px)] hd:top-2 hd:left-2",
          isOpenChapterSidebar && "w-[280px] border-r border-innoveraZinc-100 shadow left-2 z-[100]"
        )}
      >
        <div
          data-testid="sidebar-chapters-list"
          className={cn("flex flex-col gap-1 w-full overflow-y-auto h-full", "custom-thin-scrollbar")}
        >
          <SidebarDashboardItem
            isActive={viewMode === "dashboard" || viewMode === "projectOverview"}
            handleDashboardClick={toggleDashboard}
            isSidebarOpen={isOpenChapterSidebar}
            isLoading={isAnyOfTheChaptersLoading}
            isProjectOverviewMode={
              !chapterMemoPolling.isPolling && projectChapters.every((chapter) => !!chapter.activeMemo)
            }
          />

          {sidebarChaptersData.map((chapter) => {
            if (chapter.isHidden && !isLabs) return null;

            const chapterMemoStatus = chapterMemoPolling.chapters.find((c) => c.id === Number(chapter.id));
            const hasActiveMemo = chapterMemoStatus?.hasActiveMemo ?? true;
            const hasNotification = hasChapterNotification(Number(chapter.id));

            return (
              <SidebarChapterItem
                key={chapter.id}
                isSidebarOpen={isOpenChapterSidebar}
                chapter={chapter}
                isActive={activeChapter?.id === Number(chapter.id)}
                isExpanded={expandedIds.has(chapter.id)}
                handleChapterChange={handleChapterChange}
                handleSubsectionClick={handleSubsectionClick}
                activeSubsection={activeChapterSubsection}
                hasActiveMemo={hasActiveMemo}
                isPolling={chapterMemoPolling.isPolling}
                hasNotification={hasNotification}
              />
            );
          })}
        </div>
        <div className="mt-auto mr-auto">
          {isLabs && ["chapter", "instructions"].includes(viewMode) && (
            <AppButton
              onClick={toggleInstructions}
              data-testid="instructions-toggle-button"
              title="Toggle Instructions"
              appearance="text"
              className={cn(
                "w-[48px] py-2 flex justify-center items-center",
                viewMode === "instructions" && "bg-innoveraZinc-100"
              )}
            >
              <InstructionsIcon />
            </AppButton>
          )}
          <AppButton
            onClick={toggleSidebar}
            data-testid="sidebar-toggle-button"
            title="Toggle Sidebar"
            appearance="text"
            className="w-[48px] py-2 flex justify-center items-center"
          >
            <CaretRightIcon className={cn("transition-transform duration-300", isOpenChapterSidebar && "rotate-180")} />
          </AppButton>
        </div>
      </div>
    </ClickAwayListener>
  );
}
